import { Loader2 } from "lucide-react"
import React from "react"
import { cn } from "../../utils/cn"

export type ButtonVariant =
    | "primary"
    | "danger"
    | "success"
    | "warning"
    | "outline"
    | "ghost"

export type ButtonSize = "sm" | "md" | "icon"

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant
    size?: ButtonSize
    loading?: boolean
    leftIcon?: React.ReactNode
}

const base =
    "inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors select-none " +
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 " +
    "focus-visible:ring-offset-bg disabled:opacity-50 disabled:cursor-not-allowed"

const variantClasses: Record<ButtonVariant, string> = {
    primary: "bg-primary text-primary-fg hover:bg-primary/90",
    danger: "bg-danger text-white hover:bg-danger/90",
    success: "bg-success text-white hover:bg-success/90",
    warning: "bg-warning text-white hover:bg-warning/90",
    outline: "border border-border-strong text-text hover:bg-elevated",
    ghost: "text-muted hover:bg-elevated hover:text-text",
}

const sizeClasses: Record<ButtonSize, string> = {
    sm: "text-sm px-3 py-1.5",
    md: "text-sm px-4 py-2",
    icon: "p-2",
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            variant = "primary",
            size = "md",
            loading = false,
            leftIcon,
            className,
            children,
            disabled,
            ...props
        },
        ref
    ) => {
        return (
            <button
                ref={ref}
                disabled={disabled || loading}
                className={cn(
                    base,
                    variantClasses[variant],
                    sizeClasses[size],
                    className
                )}
                {...props}
            >
                {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                    leftIcon
                )}
                {children}
            </button>
        )
    }
)

Button.displayName = "Button"

export default Button
